/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable consistent-return */
import { useState } from 'react';
import { isAfter } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';

import { paths } from 'src/routes/paths';

import { handleDecrypt } from 'src/utils/cryptoPassword';
import localStorageUtil from 'src/utils/localStorageUtil';
import { callAutoLoginNative } from 'src/utils/autoLoginUtil';
import { callLoginNative } from 'src/utils/loginIntergrationUtil';
import { getTenantRedirectUri } from 'src/utils/multi-tenant-uri';
import { LOGIN_FLOW_QUERY } from 'src/utils/constants/loginConstants';
import { storageConstants } from 'src/utils/constants/storageConstants';
import { blockUserTypeConstants } from 'src/utils/enum/blockUserTypeConstants';

import store from 'src/store';
import popupApi from 'src/api/popupApi';
import memberApi from 'src/api/membershipApi';

import useCommon from './use-common';
import { useRoute } from './use-route';
import { useModal } from './use-modal';

/**
 * 작업 히스토리: 학습자 스테이지 로그인 Something Went Wrong 오류 수정
 * - 구현: storeTransfer 조건을 !!(granter || grantee) 기반으로 변경 (빈 객체 {} 통과 차단)
 * - 핵심 결정: 테스트 격리를 위해 module-level named export로 분리
 * - 참고: React Hook은 일반 함수에서 호출 불가 → pushNavigate를 DI로 주입 (S6440)
 *
 * @see TASK-241 - learner stage login error fix
 * @created 2026-07-14
 */
export const checkStoreTransfer = (
  learnerLoginResult: any,
  pushNavigate: (path: string) => void
): boolean => {
  console.log('[USER CHECK Function]storeTransfer');
  if (learnerLoginResult?.storeTransfer?.granter || learnerLoginResult?.storeTransfer?.grantee) {
    pushNavigate(`${paths.auth.storetransfer}?${LOGIN_FLOW_QUERY.KEY}=${LOGIN_FLOW_QUERY.VALUE}`);
    return true;
  }
  return false;
};

export default function useUser() {
  const location = useLocation();
  const route = useRoute();
  const common = useCommon();
  const { openModal } = useModal();
  const { t } = useTranslation();

  const { enqueueSnackbar } = useSnackbar();

  const login = useAtomValue(store.login.state);

  const [userStep, setUserStep] = useState<any>({
    dormancy: false,
    termsAgree: false,
    emailAuth: false,
    passwordChange: false,
    passwordChangeReIssue: false,
    responsibilityAuth: false,
    storeTransfer: false,
  });

  const getUserBackgroundColor = () => localStorage.getItem(storageConstants.bgColors);

  const getUserFirstChar = () => localStorage.getItem(storageConstants.firstChar);

  const checkUserStep = (type: any) => {
    console.group('[CHECK USER]Fucntion checkUserStep');
    console.log(type);
    setUserStep((prev: any) => ({ ...prev, [type]: true }));
    console.log(userStep);
    console.groupEnd();
  };

  const checkUser = async (learnerLoginResult: any) => {
    // 1. 유저 체크 함수
    const checkUserStateResult = checkUserState(learnerLoginResult);
    if (!checkUserStateResult) return checkUserStateResult;

    // 2. 자동로그인(네이티브 앱에 유저데이터 저장) 실행
    callAutoLoginNative();

    // 3. 통합로그인 체크
    if (
      localStorage.getItem(storageConstants.totalLogin) ||
      sessionStorage.getItem(storageConstants.totalLogin)
    ) {
      const totalLoginToken = localStorageUtil.getTotalLoginToken();
      callLoginNative(totalLoginToken);
      localStorage.removeItem(storageConstants.totalLogin);
      sessionStorage.removeItem(storageConstants.totalLogin);
      localStorageUtil.removeTotalLoginToken();
    }

    // 4. 리다이렉트 체크
    const redirectResult = await handleRedirect();
    console.log('[CHECK USER] 리다이렉트 결과 : ', redirectResult);
  };

  const checkUserState = (learnerLoginResult?: any) => {
    const dormancyResult = checkDormancyUser(learnerLoginResult);
    if (dormancyResult) return false;

    const termsAgreementResult = checkTermsAgreement(learnerLoginResult);
    if (termsAgreementResult) return false;

    const emailAuthResult = checkEmailAuth(learnerLoginResult);
    if (emailAuthResult) return false;

    const passwordChangeResult = checkPasswordChange(learnerLoginResult);
    if (passwordChangeResult) return false;

    const passwordReIssueResult = checkPasswordReIssue(learnerLoginResult);
    if (passwordReIssueResult) return false;

    const responsibilityAuthResult = checkResponsibilityAuth(learnerLoginResult);
    if (responsibilityAuthResult) return false;

    const storeTransferResult = checkStoreTransfer(learnerLoginResult, route.pushNavigate);
    if (storeTransferResult) return false;

    return true;
  };

  const checkDormancyUser = (learnerLoginResult: any) => {
    if (!userStep.dormancy && learnerLoginResult?.dormancy) {
      route.pushNavigate(paths.auth.dormancy);
      return true;
    }
    return false;
  };

  const checkTermsAgreement = (learnerLoginResult: any) => {
    if (!userStep.termsAgree && !learnerLoginResult?.termsAgree) {
      try {
        route.pushNavigate(`${paths.auth.termsAgree}?${LOGIN_FLOW_QUERY.KEY}=${LOGIN_FLOW_QUERY.VALUE}`);
      } catch (error) {
        console.log('TERMS ERROR : ', error);
      }
      return true;
    }
    return false;
  };

  const checkEmailAuth = (learnerLoginResult: any) => {
    if (!userStep.emailAuth && !learnerLoginResult?.emailAuth) {
      openModal({
        type: 'confirm',
        data: {
          title: t('learner.804'),
          content: `${t('learner.805')} ${t('learner.806')}`,
          accept: t('learner.835'),
          onAccept: async () => {
            checkUserStep('emailAuth');
            await loginAndRedirectToEmailConfirm();
          },
          cancel: t('learner.834'),
          onCancel: () => {
            route.pushNavigate(paths.login);
          },
          buttonColor: 'inherit',
          actionButtonsDirection: 'ltr',
          cancelButtonProps: { variant: 'outlined' },
        },
      });
      return true;
    }
    console.log('[checkEmailAuth] Skipped');
    return false;
  };

  const loginAndRedirectToEmailConfirm = async () => {
    try {
      route.pushNavigate(`${paths.auth.emailVerfication.root}?${LOGIN_FLOW_QUERY.KEY}=${LOGIN_FLOW_QUERY.VALUE}`);
    } catch (error) {
      console.log('EMAIL_AUTH_LOGIN_ERROR', error);
    }
  };

  const checkPasswordChange = (learnerLoginResult: any) => {
    console.log(
      '[USER CHECK Function]checkPasswordChange : ',
      learnerLoginResult?.changePasswordRequest
    );
    if (!localStorageUtil.isSkipPassword() && learnerLoginResult?.changePasswordRequest) {
      route.pushNavigate(paths.member.password.passwordChange);
      return true;
    }
    return false;
  };

  const checkPasswordReIssue = (learnerLoginResult: any) => {
    console.log('[USER CHECK Function]reissue : ', learnerLoginResult?.reIssueChangePassword);
    if (learnerLoginResult?.reIssueChangePassword) {
      route.pushNavigate(paths.member.password.reissueChangePassword);
      return true;
    }
    return false;
  };

  const checkResponsibilityAuth = (learnerLoginResult: any) => {
    console.log(
      '[USER CHECK Function]responsibilityAuth : ',
      learnerLoginResult.responsibilityAuth
    );
    if (!learnerLoginResult.responsibilityAuth) {
      openModal({
        type: 'confirm',
        data: {
          title: t('learner.830'),
          content: `${t('learner.831')} ${t('learner.832')}`,
          accept: t('learner.833'),
          onAccept: () => {
            route.pushNavigate(paths.auth.verificationcode);
          },
          cancel: t('learner.834'),
          onCancel: () => {
            route.pushNavigate(paths.login);
          },
          buttonColor: 'inherit',
          actionButtonsDirection: 'ltr',
          cancelButtonProps: { variant: 'outlined' },
        },
      });
      return true;
    }
    return false;
  };

  const fetchPopupData = async () => {
    const response = await popupApi.fetchpopupDetail();
    const popupData = response.data.data;
    return popupData.length > 0 ? popupData : null;
  };

  const buildFinalRedirectUrl = (replacedPath: string, redirectQueries: URLSearchParams) => {
    const hasQuery = replacedPath.includes('?');
    const queryString = redirectQueries.toString();
    return hasQuery ? `${replacedPath}&${queryString}` : `${replacedPath}?${queryString}`;
  };

  const getReplacedRedirectPath = (redirectPath: string) => {
    const { origin } = window.location;
    return redirectPath.replace(origin, '');
  };

  const adjustPath = (path: string) => {
    const contextPath = `/${getTenantRedirectUri() || ''}`;
    // localhost 환경 (contextPath === '/')에서는 경로를 그대로 반환
    if (window.location.hostname === 'localhost') {
      return path;
    }
    // 멀티테넌시 환경에서는 contextPath 제거
    return path.startsWith(contextPath) ? path.replace(contextPath, '') : path;
  };

  const handleRedirect = async () => {
    common.callPushNotification();

    try {
      const queries = new URLSearchParams(location.search);
      const redirectPath = queries.get('redirectPath');
      const redirectQueries = new URLSearchParams(location.search);
      redirectQueries.delete('redirectPath');

      console.group('[RedirectHandler] 시작');
      console.log('[RedirectHandler] Extracted redirectPath:', redirectPath);

      const popupData = await fetchPopupData();
      const showPopupDate = localStorage.getItem(storageConstants.showPopupDate);
      const isPopupDue = showPopupDate ? isAfter(Date.now(), Number(showPopupDate)) : true;

      if (redirectPath) {
        console.group('[RedirectHandler] 리다이렉트 조건 진입');

        let path = getReplacedRedirectPath(redirectPath);
        console.log('[RedirectHandler] origin 제거 후:', path);

        path = adjustPath(path);
        console.log('[RedirectHandler] contextPath 제거 후:', path);

        const finalUrl = buildFinalRedirectUrl(path, redirectQueries);
        console.log('[RedirectHandler] 최종 Redirect URL:', finalUrl);

        route.pushNavigate(finalUrl);
        console.groupEnd();
      } else if (popupData && isPopupDue) {
        console.group('[RedirectHandler] 팝업 조건 진입');

        openModal({ type: 'popup', data: {} });
        route.pushNavigate(paths.home);
        console.groupEnd();
      } else {
        route.pushNavigate(paths.home);
      }
    } catch (error) {
      console.error('[RedirectHandler] 리다이렉트 처리 중 오류 발생:', error);
    }
  };

  const setParams = (loginType: any) => {
    let params = null;

    // Due to the state not consistent so I move "userParams" to here
    const userParams = JSON.parse(localStorage.getItem(storageConstants.userParams) ?? '{}');

    if (loginType === 'social') {
      params = {
        userId: '',
        password: '',
        device: {
          type: userParams !== null ? userParams.device.type.toUpperCase() : 'WEB',
        },
        social: {
          type: 'firebase',
          token: login.socialToken,
        },
      };
    } else {
      params = {
        userId: userParams.userId,
        password: handleDecrypt(userParams.password),
        device: {
          type: userParams !== null ? userParams.device.type.toUpperCase() : 'WEB',
        },
      };
    }
    return params;
  };

  const checkDeactivateUser = (userData: any) => {
    const deActivatedList = ['WITHDRAWAL', 'DORMANCY'];
    const isDeactivatedUser = deActivatedList.includes(userData.status.value);
    return isDeactivatedUser;
  };

  const handleUserName = (userData: any) => {
    if (userData?.name) {
      const nameObj = {
        first: userData.name ? userData.name.first : userData.first,
        last: userData.name ? userData.name.last : userData.last,
        fullName: userData.name ? userData.name.fullName : userData.fullName,
      };

      return t('learner.3437', {
        first: nameObj.first || '',
        last: nameObj.last || '',
      });
    }
  };

  const handleMaskingEmail = (email: string) => {
    const id = email.split('@')[0];
    if (id.length > 2) {
      const domain = email.split('@')[1];
      return `${email.substring(0, 3)}${'*'.repeat(id.length - 3)}@${domain}`;
    }
    return email;
  };

  const setUserStatusdata = (loginResponse: any) => {
    setUserStep({
      dormancy: loginResponse.dormancy,
      emailAuth: loginResponse.emailAuth,
      termsAgree: loginResponse.termsAgree,
      storeTransfer: !!loginResponse.storeTransfer,
      passwordChange: loginResponse.passwordChange,
      responsibilityAuth: loginResponse.responsibilityAuth,
      passwordChangeReIssue: loginResponse.passwordChangeReIssue,
    });
  };

  const blockUser = async (item: any, isBlocked: any, type?: any, onSuccess?: VoidFunction) => {
    // 01. Declare `target` to store `item.auditor.register` or `item.writer`
    let target: { id?: string; name?: { last: string; first: string } } = {};

    target = item.auditor ? item.auditor.register : item.writer || {};

    // 02. Set `target.id` and `target.name` depending on the value of `type`
    if (type === blockUserTypeConstants.REVIEW) {
      target.id = item.reviewer?.memberId;
      target.name = item.reviewer?.name;
    }

    const targetId = target.id;
    const targetName = target.name;

    if (!targetId || !targetName) return;

    // 03. Set `dialogTitle` and `dialogContent`
    const dialogTitle = isBlocked
      ? t('learner.3816')
      : t('learner.3809', {
          str: t('learner.3437', { last: targetName.last, first: targetName.first }),
        });

    const dialogContent = isBlocked
      ? t('learner.3817')
      : t('learner.3810', {
          str: t('learner.3437', { last: targetName.last, first: targetName.first }),
        });

    // 04. Set `acceptText` and `cancelText`
    const acceptText = isBlocked ? t('learner.3818') : t('learner.3811');
    const cancelText = isBlocked ? t('learner.3819') : t('learner.3812');

    const onAccept = async () => {
      try {
        // 05. Call `memberApi.requestUserBlock` or `memberApi.restoreBlock`
        const blockAction = isBlocked
          ? await memberApi.restoreBlock(targetId)
          : await memberApi.requestUserBlock(targetId);

        if (blockAction) {
          enqueueSnackbar(
            isBlocked
              ? t('learner.3820', {
                  str: t('learner.3437', { last: targetName.last, first: targetName.first }),
                })
              : t('learner.3813')
          );
          onSuccess?.();
        }
      } catch (e) {
        console.error('Block action failed:', e);
      }
    };

    openModal({
      type: 'confirm',
      data: {
        title: dialogTitle,
        content: dialogContent,
        accept: acceptText,
        cancel: cancelText,
        onAccept,
        buttonColor: 'inherit',
        actionButtonsDirection: 'ltr',
        cancelButtonProps: { variant: 'outlined' },
      },
    });
  };

  return {
    checkUser,
    setUserStatusdata,
    checkDeactivateUser,
    handleUserName,
    handleMaskingEmail,
    checkPasswordChange,
    getUserBackgroundColor,
    getUserFirstChar,
    checkUserStep,
    blockUser,
  };
}
